import { Center, Flex } from '@web/components/atoms/Layout'
import { Branding } from '@web/components/utils/branding/useBrandingsStore'
import { ComponentType, type Page } from '@web/graphql-queries/graphql'
import { toBrandCssVariable, useColorWithOpacity } from '@web/components/molecules/ColorPicker/utils'

type PageLayoutVariables = {
  branding: Branding | null
  layout: Page | null
}

const PageLayoutVariables = ({ branding, layout, children }: React.PropsWithChildren<PageLayoutVariables>) => {
  const pageOptions = layout?.components?.find((c) => c.type === ComponentType.PageOptions)
  const { colorData, fontData, buttonData } = branding ?? { colorData: {}, fontData: {}, buttonData: {} }
  const pageBackgroundColor = useColorWithOpacity(
    pageOptions?.styles?.backgroundColor,
    pageOptions?.styles?.backgroundColorOpacity,
    colorData['Background'] ?? 'ge.white',
  )
  const pageMobileBackgroundColor = useColorWithOpacity(
    pageOptions?.mobileStyles?.backgroundColor,
    pageOptions?.mobileStyles?.backgroundColorOpacity,
    colorData['Background'] ?? 'ge.white',
  )
  const primaryButtonColors = buttonData.buttonColors?.['Primary']
  const secondaryButtonColors = buttonData.buttonColors?.['Secondary']

  return (
    <Center flexDirection="column" position="relative">
      <Flex
        id="page-layout-variables"
        color={colorData['Text dark'] ?? 'ge.text.primary'}
        direction="column"
        alignItems="center"
        width="100%"
        css={{
          bg: pageBackgroundColor,
          containerType: 'inline-size',
          containerName: 'page',
          '*, ::before, ::after': {
            borderColor: 'transparent',
          },
          '--branding-button-radius': `${buttonData.cornerRadius}px`,
          '--branding-button-font-family': buttonData.font?.font,
          '--branding-button-font-size': buttonData.font?.fontSize,
          '--branding-button-primary-bg-color': primaryButtonColors?.bgColor,
          '--branding-button-primary-text-color': primaryButtonColors?.textColor ?? '#FFFFFF',
          '--branding-button-primary-outline-color': primaryButtonColors?.outlineColor ?? primaryButtonColors?.bgColor,
          '--branding-button-primary-hover-bg-color': primaryButtonColors?.hoverBgColor,
          '--branding-button-primary-hover-text-color': primaryButtonColors?.hoverTextColor ?? '#FFFFFF',
          '--branding-button-primary-hover-outline-color':
            primaryButtonColors?.hoverOutlineColor ?? primaryButtonColors?.hoverBgColor,
          '--branding-button-secondary-bg-color': secondaryButtonColors?.bgColor,
          '--branding-button-secondary-hover-bg-color': secondaryButtonColors?.hoverBgColor,
          '--branding-title-font-family': fontData['heading1'].font,
          '--branding-title-font-size': fontData['heading1'].fontSize,
          '--branding-section-title-font-family': fontData['heading2'].font,
          '--branding-section-title-font-size': fontData['heading2'].fontSize,
          '--branding-body-text-font-family': fontData['normal'].font,
          '--branding-body-text-font-size': fontData['normal'].fontSize,
          ...Object.keys(colorData)
            .map((key) => ({ [toBrandCssVariable(key)]: colorData[key] }))
            .reduce((acc, val) => Object.assign(acc, val), {}),
          '@media screen and (max-width: 900px)': {
            bg: pageMobileBackgroundColor,
            '--branding-title-font-size': fontData['heading1'].mobileFontSize ?? fontData['heading1'].fontSize,
            '--branding-section-title-font-size': fontData['heading2'].mobileFontSize ?? fontData['heading2'].fontSize,
            '--branding-body-text-font-size': fontData['normal'].mobileFontSize ?? fontData['normal'].fontSize,
          },
        }}
      >
        {children}
      </Flex>
    </Center>
  )
}

export default PageLayoutVariables
